import { useLayoutEffect, useState, type ReactElement, type RefObject } from 'react';
import { createPortal } from 'react-dom';

import type { VocabTerm, VocabularyDisplayDto } from '@/api/vocabularies';

import { VocabTermInfoContent } from './VocabTermInfo';

// A comfortable reading width (px) for the verbose detail text. Doubles as the "decently
// long" threshold: an anchor already at least this wide gives the text enough room, so the
// panel just matches the anchor; a narrower anchor lets the panel grow up to this width.
const INLINE_COMFORTABLE_WIDTH = 500;
// Gap kept between the panel and the viewport edge when it overhangs.
const VIEWPORT_MARGIN = 8;

interface InlineGeom {
  readonly top: number;
  readonly left: number;
  readonly width: number;
  readonly maxHeight: number;
  readonly placement: 'above' | 'below';
}

/**
 * Width for the inline detail panel. Goal: give the verbose detail text as much useful room as
 * possible while staying inside the viewport (tablet/phone included). The panel targets a
 * comfortable reading width ({@link INLINE_COMFORTABLE_WIDTH}) — a narrow anchor lets it grow up
 * to that (overhanging the anchor), while an anchor already at least that wide just matches the
 * anchor (no point overhanging a roomy one). That target is then clamped to the horizontal space
 * actually available (the panel can overhang to the right of the anchor, or to the left), so on
 * small screens it narrows smoothly to the anchor/viewport width. The width is a continuous
 * function of the anchor width and the viewport — it never jumps as the window is resized.
 */
function inlinePanelWidth(anchor: DOMRect): number {
  const desired = Math.max(anchor.width, INLINE_COMFORTABLE_WIDTH);
  const roomOverhangingRight = window.innerWidth - VIEWPORT_MARGIN - anchor.left;
  const roomOverhangingLeft = anchor.right - VIEWPORT_MARGIN;
  const roomOnScreen = Math.max(roomOverhangingRight, roomOverhangingLeft);
  return Math.min(desired, roomOnScreen);
}

interface InlineTermInfoPanelProps {
  readonly term: VocabTerm;
  readonly display?: VocabularyDisplayDto | null;
  /** Source vocabulary name + version for the info header (optional). */
  readonly vocabularyName?: string | null;
  readonly version?: string | null;
  /**
   * Element the panel's width + horizontal position derive from — the search results list in
   * the picker, or the phenotype entry row on the patient page. Its left edge anchors the
   * panel, which overhangs right (or left, when right would run off-screen).
   */
  readonly widthAnchorRef: RefObject<HTMLElement | null>;
  /**
   * Element the panel drops below (flipping above when it wouldn't fit). Defaults to
   * {@link widthAnchorRef} — pass a separate element when the thing the panel should hang off
   * vertically differs from the width source (e.g. the picker hangs off the expanded row but
   * sizes to the whole list).
   */
  readonly positionAnchorRef?: RefObject<HTMLElement | null>;
  /**
   * Attached to the rendered panel. The caller keeps this ref so it can exempt the panel from
   * its own outside-click handling (the panel is portalled to `<body>`, outside the trigger's
   * subtree). The component also reads it to measure the panel's natural height.
   */
  readonly panelRef: RefObject<HTMLDivElement | null>;
}

/**
 * The floating term-detail panel: a `VocabTermInfoContent` body positioned relative to an
 * anchor and portalled to `<body>` so nothing clips it. Two-pass: it first paints off-screen at
 * natural height to be measured, then positions itself below the anchor — flipping above when
 * the full text wouldn't fit below — at a comfortable reading width clamped to the viewport.
 * Repositions on scroll/resize. Shared by the search picker and the patient phenotype row, which
 * differ only in the anchor they pass.
 */
export function InlineTermInfoPanel({
  term,
  display,
  vocabularyName,
  version,
  widthAnchorRef,
  positionAnchorRef,
  panelRef,
}: InlineTermInfoPanelProps): ReactElement | null {
  const [geom, setGeom] = useState<InlineGeom | null>(null);

  useLayoutEffect(() => {
    const recompute = (): void => {
      const widthEl = widthAnchorRef.current;
      const posEl = (positionAnchorRef ?? widthAnchorRef).current;
      if (widthEl === null || posEl === null) return;
      const w = widthEl.getBoundingClientRect();
      const p = posEl.getBoundingClientRect();
      const width = inlinePanelWidth(w);
      let left = w.left; // aligned to the anchor's left -> overhang on the right
      if (left + width > window.innerWidth - VIEWPORT_MARGIN) {
        left = w.right - width; // would be cut off on the right -> align right, overhang left
      }
      const panelH = panelRef.current?.scrollHeight ?? 0;
      const spaceBelow = window.innerHeight - p.bottom - 8;
      const spaceAbove = p.top - 8;
      if (panelH <= spaceBelow || spaceBelow >= spaceAbove) {
        setGeom({ top: p.bottom, left, width, maxHeight: Math.max(120, spaceBelow), placement: 'below' });
      } else {
        const maxHeight = Math.max(120, spaceAbove);
        setGeom({ top: p.top - Math.min(panelH, maxHeight), left, width, maxHeight, placement: 'above' });
      }
    };
    recompute();
    window.addEventListener('resize', recompute);
    window.addEventListener('scroll', recompute, true);
    return () => {
      window.removeEventListener('resize', recompute);
      window.removeEventListener('scroll', recompute, true);
    };
    // term.id: re-measure/-position when the displayed term changes.
  }, [term.id, widthAnchorRef, positionAnchorRef, panelRef]);

  // Width used while the panel is first measured (before its geometry is known).
  let measureWidth = INLINE_COMFORTABLE_WIDTH;
  if (geom === null) {
    const rect = widthAnchorRef.current?.getBoundingClientRect();
    if (rect !== undefined) measureWidth = inlinePanelWidth(rect);
  }

  const content = (
    <VocabTermInfoContent
      term={term}
      display={display ?? null}
      vocabularyName={vocabularyName ?? null}
      version={version ?? null}
    />
  );

  return createPortal(
    geom !== null ? (
      <div
        ref={panelRef}
        role="region"
        data-surface="overlay"
        style={{
          position: 'fixed',
          top: geom.top,
          left: geom.left,
          width: geom.width,
          maxHeight: geom.maxHeight,
        }}
        className="z-[60] overflow-y-auto rounded-md border border-border bg-popover p-3 shadow-lg"
      >
        {content}
      </div>
    ) : (
      // First paint: natural height, off-screen + hidden, so it can be measured.
      <div
        ref={panelRef}
        aria-hidden="true"
        style={{ position: 'fixed', top: -9999, left: 0, width: measureWidth, visibility: 'hidden' }}
        className="border bg-popover p-3"
      >
        {content}
      </div>
    ),
    document.body,
  );
}
